import colors from '../../assets/theme/colors';
import {
  getCareSourceOriginalText,
  getCareSourceProfileSuggestions,
  getCareSourceTimelinePointerCount,
  getCareSourceTimelinePointerDateKeys,
  buildCareSourceTimelineDateLabels,
  timestampToMillis,
} from './careSourceStatusModel';
import { resolveTimelineContentTitle } from '../../shared/sourceTitleResolver';

export const getCareDocumentTitle = (source = {}) => (
  source.careDocumentTitle
  || source.sourceMetadata?.fileName
  || source.metadata?.source?.fileName
  || source.fileName
  || (formatDate(source) ? `Document from ${formatDate(source)}` : 'Care document')
);

export const getCareDocumentType = (source = {}) => (
  source.careDocumentType
  || source.sourceMetadata?.documentTypeLabel
  || source.metadata?.source?.documentTypeLabel
  || 'Source'
);

const formatDate = (source = {}) => {
  const millis = timestampToMillis(source.capturedAt || source.createdAt || source.updatedAt);
  if (!millis) return '';

  const date = new Date(millis);
  const today = new Date();
  if (
    date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
  ) {
    return 'Today';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

export const getCareDocumentPurpose = (source = {}) => (
  source.documentPurpose
  || source.metadata?.careSourceProcessing?.documentPurpose
  || source.metadata?.captureFirst?.documentPurpose
  || null
);

export const getCareDocumentPurposeLabel = (source = {}) => (
  getCareDocumentPurpose(source)?.label
  || getCareDocumentPurpose(source)?.type
  || ''
);

export const getCareDocumentMeta = (source = {}) => (
  [
    getCareDocumentType(source),
    getCareDocumentPurposeLabel(source),
    formatDate(source),
  ].filter(Boolean).join(' · ')
);

const hasAssociatedTimelineStory = (source = {}) => Boolean(
  source.metadata?.careSourceProcessing?.timelineEntryIds?.length
  || source.metadata?.careSourceProcessing?.savedTimelineCards?.length
  || getCareSourceTimelinePointerCount(source) > 0
);

const getTimelineSubtitleLabel = (source = {}) => (
  buildCareSourceTimelineDateLabels(getCareSourceTimelinePointerDateKeys(source))[0]
  || ''
);

export const getCareDocumentTimelineTitle = (source = {}) => {
  if (!hasAssociatedTimelineStory(source)) return '';
  const contentTitle = resolveTimelineContentTitle(source);
  const timelineSubtitleLabel = getTimelineSubtitleLabel(source);
  if (contentTitle && contentTitle !== getCareDocumentTitle(source)) {
    return contentTitle;
  }
  return timelineSubtitleLabel || '';
};

const profileSuggestionCardByKey = {
  about: {
    id: 'profile_updates',
    bucket: 'profile',
    bucketLabel: 'Profile',
    title: 'Profile Updates',
  },
  earlyWarningSigns: {
    id: 'warning_signs',
    bucket: 'behavior',
    bucketLabel: 'Behavior',
    title: 'Warning Signs',
  },
  thingsThatUsuallyHelp: {
    id: 'de_escalation_strategies',
    bucket: 'behavior',
    bucketLabel: 'Behavior',
    title: 'De-escalation Strategies',
  },
  thingsToAvoid: {
    id: 'known_triggers',
    bucket: 'behavior',
    bucketLabel: 'Behavior',
    title: 'Known Triggers',
  },
  safetyConsiderations: {
    id: 'safety_plan',
    bucket: 'safety',
    bucketLabel: 'Safety',
    title: 'Safety Plan',
  },
  communicationStyle: {
    id: 'communication_profile',
    bucket: 'communication',
    bucketLabel: 'Communication',
    title: 'Communication Profile',
  },
  communicationNotes: {
    id: 'communication_profile',
    bucket: 'communication',
    bucketLabel: 'Communication',
    title: 'Communication Profile',
  },
  familySupportTips: {
    id: 'daily_routine_supports',
    bucket: 'daily_living',
    bucketLabel: 'Daily Living',
    title: 'Daily Routine Supports',
  },
  schoolTherapySuggestions: {
    id: 'current_services',
    bucket: 'therapies_services',
    bucketLabel: 'Therapies & Services',
    title: 'Current Services',
  },
};

const destinationLabelByType = {
  timeline: {
    bucket: 'timeline',
    bucketLabel: 'Timeline',
    title: 'Extracted events',
  },
  goals: {
    bucket: 'goals',
    bucketLabel: 'Goals',
    title: 'Extracted goals',
  },
  profile_or_knowledge: {
    bucket: 'profile_or_knowledge',
    bucketLabel: 'Profile & knowledge',
    title: 'Reusable information',
  },
  structured_review: {
    bucket: 'structured_review',
    bucketLabel: 'Needs review',
    title: 'Needs review',
  },
  library_only: {
    bucket: 'library_only',
    bucketLabel: 'Library',
    title: 'Document details',
  },
  needs_resolution: {
    bucket: 'needs_resolution',
    bucketLabel: 'Needs interpretation',
    title: 'Needs interpretation',
  },
};

const normalizeDestinationCandidates = (source = {}) => {
  const safeSource = source || {};
  const candidates = Array.isArray(safeSource.documentDestinationCandidates)
    ? safeSource.documentDestinationCandidates
    : [];

  return candidates
    .filter((candidate) => candidate && candidate.destination && candidate.destination !== 'none' && candidate.destination !== 'needs_resolution')
    .map((candidate, index) => ({
      id: candidate.key || `${candidate.sourceFactCandidateKey || source.id || 'document'}-${index}`,
      destination: candidate.destination,
      title: candidate.extracted?.title || candidate.label || candidate.sourceText || 'Document detail',
      text: candidate.extracted?.description
        || candidate.extracted?.body
        || candidate.sourceText
        || candidate.label
        || '',
      sourceText: candidate.sourceText || '',
      confidence: candidate.destinationConfidence || candidate.factTypeConfidence || candidate.extractionConfidence || '',
      needsReview: candidate.destination === 'structured_review',
      provenance: candidate.provenance || {},
      findingMetadata: candidate.findingMetadata || {},
      sourceFactCandidateKey: candidate.sourceFactCandidateKey || '',
      factType: candidate.factType || '',
      resolutionReason: candidate.resolutionReason || '',
      resolutionSignals: Array.isArray(candidate.resolutionSignals) ? candidate.resolutionSignals : [],
    }))
    .filter((item) => item.title || item.text);
};

const normalizeDisplayCards = (cards = []) => {
  if (!Array.isArray(cards)) return [];

  return cards
    .map((card, cardIndex) => {
      const items = Array.isArray(card?.items)
        ? card.items
          .map((item, itemIndex) => ({
            id: item?.id || item?.sourceItemIds?.[0] || `${card?.id || 'card'}-${itemIndex}`,
            text: String(item?.text || '').trim(),
            sourceText: String(item?.sourceText || '').trim(),
            confidence: String(item?.confidence || '').trim(),
          }))
          .filter((item) => item.text)
        : [];

      return {
        id: card?.id || `organized-card-${cardIndex}`,
        bucket: card?.bucket || 'other',
        bucketLabel: card?.bucketLabel || 'Other',
        title: String(card?.title || '').trim() || 'Care Notes',
        items,
      };
    })
    .filter((card) => card.items.length > 0);
};

const buildDestinationCards = (source = {}) => {
  const items = normalizeDestinationCandidates(source);
  if (!items.length) return [];

  const groups = new Map();
  items.forEach((item) => {
    const config = destinationLabelByType[item.destination] || destinationLabelByType.library_only;
    const groupId = item.destination;
    if (!groups.has(groupId)) {
      groups.set(groupId, {
        id: groupId,
        bucket: config.bucket,
        bucketLabel: config.bucketLabel,
        title: config.title,
        items: [],
      });
    }
    groups.get(groupId).items.push(item);
  });

  return [...groups.values()].map((group) => ({
    ...group,
    items: group.items,
  }));
};

export const getCareDocumentResolvedGroups = (source = {}) => buildDestinationCards(source);

export const getCareDocumentOrganizedKnowledgeCards = (source = {}) => {
  const resolvedGroups = getCareDocumentResolvedGroups(source);
  if (resolvedGroups.length) return resolvedGroups;

  const cards = source.organizedCareKnowledgeCards
    || source.careKnowledgeSuggestionSummary?.organizedKnowledgeCards
    || [];
  const normalizedCards = normalizeDisplayCards(cards);
  return normalizedCards.length ? normalizedCards : getProfileSuggestionCards(source);
};

export const getCareDocumentResolvedSummary = (source = {}) => {
  const groups = getCareDocumentResolvedGroups(source);
  if (!groups.length) return '';

  return groups.map((group) => `${group.bucketLabel} ${group.items.length}`).join(' · ');
};

const getProfileSuggestionCards = (source = {}) => {
  const cardsById = new Map();

  getCareSourceProfileSuggestions(source).forEach((suggestion) => {
    const cardConfig = profileSuggestionCardByKey[suggestion.key] || profileSuggestionCardByKey.about;
    if (!cardsById.has(cardConfig.id)) {
      cardsById.set(cardConfig.id, {
        ...cardConfig,
        items: [],
      });
    }

    const card = cardsById.get(cardConfig.id);
    suggestion.items.forEach((text, itemIndex) => {
      card.items.push({
        id: `${suggestion.key}-${itemIndex}`,
        text,
        sourceText: '',
        confidence: source.profileSuggestionSummary?.confidence || '',
      });
    });
  });

  return [...cardsById.values()].filter((card) => card.items.length > 0);
};

export const getCareDocumentSearchText = (source = {}) => [
  getCareDocumentTitle(source),
  getCareDocumentType(source),
  getCareDocumentPurposeLabel(source),
  getCareDocumentMeta(source),
  getCareDocumentOriginalText(source),
  getCareDocumentResolvedSummary(source),
  ...getCareDocumentOrganizedKnowledgeCards(source).flatMap((card) => [
    card.title,
    card.bucketLabel,
    ...card.items.map((item) => item.text),
  ]),
].join(' ').toLowerCase();

export const getCareDocumentOriginalText = (source = {}) => (
  getCareSourceOriginalText(source).trim()
);

const documentThemeByType = [
  {
    match: /\bbehavior|safety\b/i,
    bg: colors.semantic.behavior.mist,
    ink: colors.semantic.categories.behavior.ink,
  },
  {
    match: /\biep|school\b/i,
    bg: colors.semantic.profileFlow.medsIconBg,
    ink: colors.semantic.profileFlow.medsIconInk,
  },
  {
    match: /\bmedication|allergy\b/i,
    bg: colors.semantic.meds.mist,
    ink: colors.semantic.meds.ink,
  },
  {
    match: /\breport|therapy\b/i,
    bg: colors.semantic.behavior.mist,
    ink: colors.semantic.behavior.ink,
  },
];

export const getCareDocumentTheme = (source = {}) => {
  const text = `${getCareDocumentTitle(source)} ${getCareDocumentType(source)}`;
  return documentThemeByType.find((theme) => theme.match.test(text)) || {
    bg: colors.semantic.sageMist,
    ink: colors.semantic.ink,
  };
};
